import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
  additionalSection3?: {
    title: string;
    content: string[];
  };
  additionalSection4?: {
    title: string;
    content: string[];
  };
}

// Define the content for the multi-step text module
const moduleContent: ModuleSection[] = [
  {
    title: 'Compassion: Strength with Softness',
    content: [
      'Compassion isn\'t about being weak or easily manipulated; it\'s about having the strength to stay open and present when faced with suffering–yours or someone else\'s. It\'s the highest form of love because it combines understanding with kindness.',
      'In your spiritual growth, the main work of this module is simple: you learn to give the same gentle understanding to your own messy, imperfect self that you would naturally give to a struggling best friend.',
    ],
  },
  {
    title: 'Deepening the Coaching Focus',
    content: [
      'The coaching conversation focuses on redirecting the client\'s energy away from self-protection and outward, loving engagement.'
    ],
    additionalSection: {
      title: 'Why Compassion is a Spiritual Game-Changer',
      content: [
        'When you practice compassion, you stop building walls and start building bridges.',
      ]
    },
    additionalSection2: {
      title: 'Healing the Inner Critic',
      content: [
        'Most people are much harder on themselves than they are on anyone else. This constant self-criticism keeps you locked in fear and shame. Compassion is the antidote. It teaches you to look at your mistakes and flaws with the eyes of a loving elder, realizing that being human means being imperfect. This acceptance is what truly heals emotional wounds and restores your sense of worth.',
      ]
    },
    additionalSection3: {
      title: 'Freeing Yourself Through Forgiveness',
      content: [
        'Forgiveness is the most powerful gift you can give yourself. When you hold onto resentment or anger toward someone (or yourself), you carry a heavy emotional weight. That weight drains your energy and keeps you stuck in the past. Forgiveness doesn\'t mean excusing a past hurt; it means deciding to release the pain\'s power over your present life. When you do, that trapped energy immediately becomes available for living and creating.',
      ]
    },
    additionalSection4: {
      title: 'The Daily Intention of Kindness',
      content: [
        'Compassion shows up every day as loving-kindness. By simply setting an intention to wish well–for yourself and others–you shift your perspective. This conscious choice moves you away from judgment and toward empathy. You start to see others not as separate threats or annoyances, but as fellow travelers dealing with their own struggles. This is how you step into a higher awareness of love.',
      ]
    }
  },
  {
      title: 'Easy Ways to Expand Your Heart',
      content: [
      'These practices take compassion from an idea to an action in your daily life.',
      '• <strong>Self-Compassion Letter:</strong> When you\'re having a hard time, sit down and write a note to yourself as if you were writing to your dearest friend who is going through the exact same struggle. Use their voice: be kind, supportive, and understanding. This simple act rewires your inner voice away from criticism.',
      '• <strong>Loving-Kindness Meditation:</strong> This is a simple mental workout for your heart. Sit quietly and silently repeat phrases of well-wishing. Start with yourself: "May I be well, may I be happy." Then extend it to someone you love, someone neutral, and finally, someone you find difficult. This opens your heart in all directions.',
      '• <strong>Compassionate Listening:</strong> This is about giving someone the gift of your undivided attention. When someone is speaking, focus entirely on hearing them. Don\'t interrupt, don\'t formulate your reply, and don\'t judge. This pure presence is a selfless act of love that instantly builds a bridge between souls.',
      '<br/>',
      '<strong>Key Message:</strong> Compassion is the realization that we are all in this together. When you choose understanding over judgment, you create peace not just in the world, but within yourself.'
    ],
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed the module content! Now, let\'s reinforce your learning with a quick quiz to check your understanding.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about compassion and expanding the heart.',
        'The quiz will help you review key concepts about self-compassion, forgiveness, and practicing loving-kindness toward yourself and others.'
      ]
    }
  },
];

// Main component, matching the design of the standard assessment form
const SpiritualModule4Page = () => {
  const navigate = useNavigate();
  const { currentStep: rawCurrentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM4');
  const totalSteps = moduleContent.length;

  // Ensure currentStep is within valid bounds
  const currentStep = Math.min(rawCurrentStep, totalSteps - 1);

  const currentModule = moduleContent[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep);
    } else {
      // Mark the module as completed and navigate to quiz
      await saveProgress(currentStep, true);
      navigate('/panel/learn/spiritual-module-4/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/spiritual-resources'); // Exit module if on first step
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep);
  };

  const isCompleted = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        
        {/* Left Panel - Illustration */}
        {/* 2. Updated left panel class: module-left-panel -> module-left */}
        <div 
          className="module-left spiritual-panel"
        >
          {/* 3. Retaining module-header for left panel content */}
          <div className="module-header">
            <h1>Module 4</h1>
            <p className="module-subtitle">Compassion – Expanding the Heart</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
            Compassion isn't about being weak or easily manipulated; it's about having the strength to stay open and present when faced with suffering–yours or someone else's. 
            It's the highest form of love because it combines understanding with kindness.
            </p>
            <p className="module-tool-info">
            In your spiritual growth, the main work of this module is simple: you learn to give the same gentle understanding to your own messy, imperfect self that you would naturally give to a struggling best friend.
            </p>
          </div>
        </div>

        {/* Right Panel - Content */}
        {/* 4. Updated right panel class: module-right-panel -> module-right */}
        <div className="module-right">
          
          {/* Content Wrapper for flex space-between layout */}
          <div className="module-content"> 
          
            {/* Progress Bar */}
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            {/* Content Section */}
            <div className="module-section">
              <h2 className="module-title"> 
                {` ${currentModule.title}`}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {/* Additional Title Sections */}
            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection3 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection3.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection3.content.map((paragraph: string, index: number) => (
                    <p key={`additional3-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection4 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection4.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection4.content.map((paragraph: string, index: number) => (
                    <p key={`additional4-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              // Logic moved to handleBack, so button is never disabled until the end state is reached.
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Back'}
            </button>
            
            <button
              className="btn-primary"
              onClick={handleNext}
            >
              {isCompleted ? 'Take the Quiz' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule4Page;

